import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifaq, IfilterProps, TfaqRequset, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../commerce-client';

/**
 * Represents a Frequently Asked Question (FAQ) in the commerce system.
 * Provides functionality to manage FAQ entries including CRUD operations and filtering.
 *
 * @class Faq
 * @extends DatabaseAuto
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Faq} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const faq = await Faq.getAll();
 *
 * const faq = await Faq.filterAll({
 *
 * });
 *
 * const faq = await Faq.bulkDelete({
 *
 * });
 *
 * const faq = await Faq.getOne('resourceId');
 *
 * const faq = await Faq.remove();
 *
 * ```
 */
export class Faq extends DatabaseAuto {
  /** Base URL for FAQ API endpoints. @private @static @readonly */
  private static readonly baseUrl = '/faq';

  /** Unique reference identifier for the FAQ. @readonly */
  readonly urId: Ifaq['urId'];

  /** User who created or is associated with the FAQ. @readonly */
  readonly user: Ifaq['user'];

  /** The question text of the FAQ. @readonly */
  readonly qn: Ifaq['qn'];

  /** The answer text of the FAQ. @readonly */
  readonly answer: Ifaq['answer'];

  /** Weight/priority of the FAQ for sorting purposes. @readonly */
  readonly weight: Ifaq['weight'];

  /** Category the FAQ belongs to. @readonly */
  readonly category: Ifaq['category'];

  /** Tags associated with the FAQ for better categorization. @readonly */
  readonly tags: Ifaq['tags'];

  /**
   * Creates a new Faq instance with the provided data.
   *
   * @constructor
   * @param {Ifaq} data - The FAQ data used to initialize the instance.
   * @throws {Error} When required fields are missing or invalid.
   *
   * @example
   * const faqData = {
   *   urId: 'faq-123',
   *   qn: 'How to reset password?',
   *   answer: 'Click on forgot password link.',
   *   weight: 1,
   *   category: 'Account',
   *   tags: ['account', 'password']
   * };
   * const faq = new Faq(faqData);
   */
  constructor(data: Ifaq) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      qn: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Question must be a non-empty string'
      }
    });

    super(data);

    this.user = data.user;
    this.urId = data.urId;
    this.qn = data.qn;
    this.answer = data.answer;
    this.weight = data.weight;
    this.category = data.category;
    this.tags = data.tags;
  }

  /**
   * Retrieves all FAQs with pagination support.
   *
   * @static
   * @async
   * @param {number} [offset=0] - The starting point for pagination.
   * @param {number} [limit=20] - The number of items to retrieve per page.
   * @returns {Promise<{ count: number, dataArr: Faq[] }>} Object containing total count and array of FAQ instances.
   * @throws {Error} When the API request fails.
   *
   * @example
   * const { count, dataArr } = await Faq.getAll(0, 10);
   * console.log(`Found ${count} FAQs`);
   */
  static async getAll(offset = 0, limit = 20): Promise<{ count: number; dataArr: Faq[] }> {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<Ifaq>>(`${Faq.baseUrl}?${query}`);
    const faqs = await lastValueFrom(observer$);

    return {
      count: faqs.count,
      dataArr: faqs.data.map(val => new Faq(val))
    };
  }

  /**
   * Filters FAQs based on the provided criteria.
   *
   * @static
   * @async
   * @param {IfilterProps} filter - The filter criteria for retrieving FAQs.
   * @returns {Promise<{ count: number, dataArr: Faq[] }>} Object containing total count and filtered FAQ instances.
   * @throws {Error} When the filter operation fails.
   *
   * @example
   * const filter = { category: 'Account', tags: ['password'] };
   * const { count, dataArr } = await Faq.filterAll(filter);
   */
  static async filterAll(filter: IfilterProps): Promise<{ count: number; dataArr: Faq[] }> {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<Ifaq>>(`${Faq.baseUrl}/filter`, filter);
    const faqs = await lastValueFrom(observer$);

    return {
      count: faqs.count,
      dataArr: faqs.data.map(val => new Faq(val))
    };
  }

  /**
   * Retrieves a single FAQ by its resource ID.
   *
   * @static
   * @async
   * @param {string} resourceId - The unique identifier of the FAQ to retrieve.
   * @returns {Promise<Faq>} The requested FAQ instance.
   * @throws {Error} When the FAQ is not found or request fails.
   *
   * @example
   * const faq = await Faq.getOne('faq-123');
   * console.log(faq.qn);
   */
  static async getOne(resourceId: string): Promise<Faq> {
    const observer$ = CommerceClient.ehttp
      .makeGet<Ifaq>(`${Faq.baseUrl}/${resourceId}`);
    const faq = await lastValueFrom(observer$);

    return new Faq(faq);
  }

  /**
   * Creates a new FAQ entry in the system.
   *
   * @static
   * @async
   * @param {TfaqRequset<true>} payload - The data for the new FAQ.
   * @returns {Promise<Faq>} The newly created FAQ instance.
   * @throws {Error} When creation fails due to invalid data or server error.
   *
   * @example
   * const newFaq = await Faq.add({
   *   qn: 'How to contact support?',
   *   answer: 'Email us at support@example.com',
   *   category: 'Support'
   * });
   */
  static async add(payload: TfaqRequset<true>): Promise<Faq> {
    const observer$ = CommerceClient.ehttp
      .makePost<Ifaq>(`${Faq.baseUrl}`, payload);

    return new Faq(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple FAQs based on the provided criteria.
   *
   * @static
   * @async
   * @param {IbulkDelete} payload - The criteria for bulk deletion.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk deletion.
   * @throws {Error} When the deletion operation fails.
   *
   * @example
   * const result = await Faq.bulkDelete({ ids: ['faq-1', 'faq-2'] });
   * console.log(`Deleted ${result.deletedCount} FAQs`);
   */
  static bulkDelete(payload: IbulkDelete): Promise<IbulkDeleteSuccessResponse> {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${Faq.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current FAQ instance with new data.
   *
   * @async
   * @param {TfaqRequset} payload - The updated FAQ data.
   * @returns {Promise<Faq>} The updated FAQ instance.
   * @throws {Error} When the update operation fails.
   *
   * @example
   * await faq.update({ answer: 'Updated answer text' });
   */
  async update(payload: TfaqRequset): Promise<Faq> {
    const observer$ = CommerceClient.ehttp
      .makePut<Ifaq>('/faq/' + this._id, payload);

    return new Faq(await lastValueFrom(observer$));
  }

  /**
   * Removes the current FAQ instance from the system.
   *
   * @async
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion.
   * @throws {Error} When the deletion operation fails.
   *
   * @example
   * const result = await faq.remove();
   * if (result?.deletedCount === 1) {
   *   console.log('FAQ deleted successfully');
   * }
   */
  remove(): Promise<IdeleteOneResponse | null> {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Faq.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
